import { TaskTypes } from "@/constants/task-types";

export class DataSource {
  /**
   *
   * @param {string} id
   * @param {string} name
   * @param {string} connectorId
   * @param {string} processingAppId
   * @param {string} organization
   * @param {string} modelId
   * @param {boolean} paused
   * @param {string} createdAt
   * @param {string} userId
   * @param {string} datasetId
   * @param {object} connectorInfo
   * @param {object} datasetInfo
   * @param {object} servableModelInfo
   * @param {object} modelInfo
   * @param {string} arcingModelId
   * @param {boolean} savePredictionsOnConnector
   */
  constructor(
    id,
    name,
    connectorId,
    processingAppId,
    organization,
    modelId,
    paused,
    createdAt,
    userId,
    datasetId,
    connectorInfo,
    datasetInfo,
    servableModelInfo,
    modelInfo,
    arcingModelId,
    applicationInfo,
    savePredictionsOnConnector
  ) {
    /**@type {string} */
    this.id = id;

    /**@type {string} */
    this.name = name;

    /**@type {string} */
    this.connectorId = connectorId;

    /**@type {string} */
    this.processingAppId = processingAppId;

    /**@type {string} */
    this.organization = organization;

    /**@type {string} */
    this.modelId = modelId;

    /**@type {boolean} */
    this.connected = !paused;

    /**@type {string} */
    this.createdAt = createdAt;

    /**@type {string} */
    this.userId = userId;

    /**@type {string} */
    this.datasetId = datasetId;

    /**@type {object} */
    this.connectorInfo = connectorInfo;

    /**@type {object} */
    this.datasetInfo = datasetInfo;

    /** @type {object} */
    this.servableModelInfo = servableModelInfo;

    /**@type {object} */
    this.modelInfo = modelInfo;

    /**@type {object} */
    this.datasetName = datasetInfo.name ?? "";

    /**@type {object} */
    this.modelName = modelInfo.name ?? "";

    /**@type {object} */
    this.applicationInfo = applicationInfo;

    /**@type {string} */
    this.arcingModelId = arcingModelId;

    /**@type {string[]} */
    this.sides = ["A", "B", "C", "D", "All"];

    this.taskType = this.getTaskType(applicationInfo?.task_type);

    this.savePredictionsOnConnector = savePredictionsOnConnector;
  }

  static newInitial() {
    return new DataSource("", "", "", "", "", "", true, "", "", "", {}, {}, {}, "");
  }

  /**
   * Map data from api to instance of DataSource
   * @param {object} data - data from api that should be mapped to this entity
   * @returns {DataSource} new instance with the mapped data
   */
  static fromApi({
    _id,
    name,
    connector_id,
    processing_app_id,
    organization,
    model_id,
    paused,
    created_at,
    user_id,
    dataset_id,
    connector_info,
    dataset_info,
    servable_model_info,
    arcing_model_id,
    application_info,
    save_predictions_on_connector,
  }) {
    return new DataSource(
      _id,
      name,
      connector_id,
      processing_app_id,
      organization,
      model_id,
      paused,
      created_at,
      user_id,
      dataset_id,
      connector_info ?? {},
      dataset_info ?? {},
      servable_model_info ?? {},
      arcing_model_id ?? "",
      application_info,
      save_predictions_on_connector
    );
  }

  getTaskType(taskType) {
    if (!taskType) return "";
    if (taskType === TaskTypes.CLASSIFICATION) return TaskTypes.CLASSIFICATION;
    // normalize all the other task types to object detection
    return TaskTypes.OBJECT_DETECTION;
  }
}

export class Prediction {
  /**
   * @param {string} path
   * @param {string} createdAt
   * @param {string} id
   * @param {string} monitorId
   * @param {object} judgment
   * @param {object[]} judgments
   * @param {object} prediction
   * @param {object} review_assignee
   * @param {string} status
   * @param {string} error
   * @param {string} updated_at
   * @param {object} model_info
   */
  constructor(
    path,
    createdAt,
    id,
    monitorId,
    judgment,
    judgments,
    prediction,
    review_assignee,
    status,
    error,
    is_file,
    updated_at,
    model_info,
    last_run_at
  ) {
    this.path = path;
    this.createdAt = createdAt;
    this.id = id;
    this.monitorId = monitorId;
    this.judgment = judgment;
    this.judgments = judgments;
    this.prediction = prediction;
    this.review_assignee = review_assignee;
    this.status = status;
    this.error = error;
    this.is_file = is_file;
    this.updated_at = updated_at;
    this.model_info = model_info;
    this.last_run_at = last_run_at;
  }

  static fromAPI({
    path,
    is_file,
    id,
    monitor_id,
    judgment,
    judgments,
    prediction,
    review_assignee,
    status,
    error,
    created_at,
    updated_at,
    model_info,
    last_run_at,
  }) {
    return new Prediction(
      path,
      created_at,
      id,
      monitor_id,
      judgment,
      judgments,
      prediction,
      review_assignee,
      status,
      error,
      is_file,
      updated_at,
      model_info,
      last_run_at
    );
  }
}

export class Adjacent {
  /**
   * @param {string|null} prevImagePath
   * @param {string|null} nextImagePath
   */
  constructor(prevImagePath, nextImagePath) {
    this.prevImagePath = prevImagePath;
    this.nextImagePath = nextImagePath;
  }

  static fromAPI({ prev_image_path, next_image_path }) {
    return new Adjacent(prev_image_path, next_image_path);
  }

  static newInitial() {
    return new Adjacent(null, null);
  }
}

export class ReviewStats {
  /**
   * @param {number} notReviewed
   * @param {number} reviewed
   * @param {number} total
   */
  constructor(notReviewed, reviewed, total) {
    this.notReviewed = notReviewed;
    this.reviewed = reviewed;
    this.total = total;
  }

  static fromAPI({ not_reviewed, reviewed, total }) {
    return new ReviewStats(not_reviewed, reviewed, total);
  }

  static newInitial() {
    return new ReviewStats(0, 0, 0);
  }
}

export class Assignment {
  constructor(assigneeId = "", count = 0, username = "", email = "") {
    /** @type {string} */
    this.assigneeId = assigneeId;

    /** @type {number} */
    this.count = count;

    /** @type {string} */
    this.username = username;

    /** @type {string} */
    this.email = email;
  }

  static fromApi = ({ assignee_id, count, user_info: { username, email } }) =>
    new Assignment(assignee_id, count, username, email);
}
